import React, { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { Alert, AlertTitle, Box, Button, Typography } from '@mui/material';
import { Refresh as RefreshIcon, BugReport as BugReportIcon } from '@mui/icons-material';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
  gridName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class GridErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Grid Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Report to error tracking service
    // Example: Sentry, LogRocket, etc.
    if (process.env.NODE_ENV === 'production') {
      // reportError(error, { 
      //   component: 'GridErrorBoundary',
      //   gridName: this.props.gridName,
      //   errorInfo 
      // });
    }
  }

  private handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });

    // Call parent reset handler if provided
    if (this.props.onReset) {
      this.props.onReset();
    }
  };

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, errorInfo } = this.state;
      const gridName = this.props.gridName || 'Grid';

      return (
        <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BugReportIcon />
                {gridName} Error
              </Box>
            </AlertTitle>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Something went wrong while rendering the {gridName.toLowerCase()}. 
              This is likely a temporary issue.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={this.handleReset}
              >
                Try Again
              </Button>
              
              <Button
                variant="outlined"
                size="small"
                onClick={this.handleReload}
              >
                Reload Page
              </Button>
            </Box>
          </Alert>

          {/* Error details for development */}
          {process.env.NODE_ENV === 'development' && error && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <AlertTitle>Development Error Details</AlertTitle>
              <Typography 
                variant="body2" 
                component="pre" 
                sx={{ 
                  fontSize: '0.75rem',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 200,
                  overflow: 'auto',
                  mt: 1,
                }}
              >
                <strong>Error:</strong> {error.message}
                {error.stack && (
                  <>
                    {'\n\n'}
                    <strong>Stack Trace:</strong>
                    {'\n'}
                    {error.stack}
                  </>
                )}
                {errorInfo?.componentStack && (
                  <>
                    {'\n\n'}
                    <strong>Component Stack:</strong>
                    {errorInfo.componentStack}
                  </>
                )}
              </Typography>
            </Alert>
          )}
        </Box>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for easier usage
export const withGridErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  gridName?: string
) => {
  const WrappedComponent = (props: P) => (
    <GridErrorBoundary gridName={gridName}>
      <Component {...props} />
    </GridErrorBoundary>
  );

  WrappedComponent.displayName = `withGridErrorBoundary(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
};

export default GridErrorBoundary;